import type { AppNotification } from '@discovr/types';

export interface DayGroup {
  label: string;
  items: AppNotification[];
}

function dayLabel(iso: string, now: Date): string {
  const d = new Date(iso);
  const startOfDay = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const diffDays = Math.round((startOfDay(now) - startOfDay(d)) / 86_400_000);
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/**
 * Groups notifications by calendar day, preserving the caller's sort order
 * both across and within groups.
 *
 * Buckets by `last_event_at` (falling back to `created_at`) rather than
 * `created_at` alone: a thread notification is one permanent document per
 * thread, so `created_at` is frozen at the thread's first-ever message while
 * `last_event_at` bumps on every reply. The list is sorted by `last_event_at`
 * server-side and every row renders relativeTime(last_event_at ||
 * created_at), so grouping on the same field keeps headers monotonic with
 * both the row order and the row's own displayed timestamp.
 */
export function groupByDay(items: AppNotification[], now: Date = new Date()): DayGroup[] {
  const groups: DayGroup[] = [];
  for (const item of items) {
    const label = dayLabel(item.last_event_at || item.created_at, now);
    const last = groups[groups.length - 1];
    if (last && last.label === label) {
      last.items.push(item);
    } else {
      groups.push({ label, items: [item] });
    }
  }
  return groups;
}
